import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowLeft, Settings } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import { NameCard } from "@/components/account/SettingsBits";
import { EmailCard, PasswordCard, PrefsCard } from "@/components/account/SettingsCards";

export default function AccountSettings() {
  const { user, loading, openAuth, setUser } = useAuth();
  const qc = useQueryClient();
  const { data: overview } = useQuery({ queryKey: ["account-overview"], queryFn: async () => (await api.get("/account/overview")).data, enabled: !!user });
  const refresh = () => qc.invalidateQueries({ queryKey: ["account-overview"] });
  const onUser = (u) => { setUser((prev) => ({ ...prev, ...u })); refresh(); };

  if (loading) return <div className="mx-auto max-w-5xl px-4 py-20"><div className="h-40 animate-pulse rounded-2xl bg-muted" /></div>;
  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-serif text-4xl font-bold text-foreground sm:text-5xl">Sign in to manage your account</h1>
        <Button data-testid="account-signin-button" onClick={openAuth} className="mt-8 rounded-full bg-primary px-8 py-6 text-base text-primary-foreground hover:bg-primary/90">Sign in</Button>
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8" data-testid="account-settings">
      <Seo title="Account settings" />
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="account-back-dashboard"><ArrowLeft className="h-3.5 w-3.5" /> Dashboard</Link>
      <header className="mb-8 mt-3">
        <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-gold"><Settings className="h-3.5 w-3.5" /> Account</span>
        <h1 className="mt-2 font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl">Account settings</h1>
        <p className="mt-2 text-sm text-muted-foreground">Your name, sign-in details and what we email you.</p>
      </header>
      {overview ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <NameCard key={overview.user.name} initial={overview.user.name} onSaved={onUser} />
          <EmailCard key={overview.user.email} email={overview.user.email} hasPassword={overview.user.has_password} onSaved={onUser} />
          <PasswordCard hasPassword={overview.user.has_password} onSaved={refresh} />
          <PrefsCard key={JSON.stringify(overview.prefs)} prefs={overview.prefs} onSaved={refresh} />
        </div>
      ) : (
        <div className="h-40 animate-pulse rounded-2xl bg-muted" />
      )}
    </div>
  );
}
